/**
 * layout — the research surface's binding of the shared floating-panel model.
 *
 * The model itself now lives in `shared/surface/panelLayout` and serves every
 * surface that wants floating panes (research, review, tables). This file is
 * research's CONFIG plus thin bindings, so callers here keep the exact API
 * they always had: `movePanel(layout, id, dock, index)` rather than passing
 * the surface through at every call site.
 */

import {
  DockSide,
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
  PanelSurface,
  activeSheet as genericActiveSheet,
  bringToFront as genericBringToFront,
  dockScrolls as genericDockScrolls,
  dockWidth as genericDockWidth,
  loadLayout as genericLoadLayout,
  movePanel as genericMovePanel,
  panelZIndex as genericPanelZIndex,
  panelsInDock as genericPanelsInDock,
  readingInset as genericReadingInset,
  resizePanel as genericResizePanel,
  saveLayout as genericSaveLayout,
  setPanelVisible as genericSetPanelVisible,
  togglePanel as genericTogglePanel,
  validateLayout as genericValidateLayout,
} from '../../shared/surface/panelLayout';

export type { DockSide };
export {
  COMFORTABLE_STACK,
  DOCK_INSET,
  SINGLE_COLUMN_BELOW,
  isSingleColumn,
} from '../../shared/surface/panelLayout';

export type PanelId = 'history' | 'document' | 'settings';
export type PanelSlot = GenericSlot<PanelId>;
export type PanelLayout = GenericLayout<PanelId>;

export const PANEL_IDS: PanelId[] = ['history', 'document', 'settings'];

/** Per-panel resize bounds. */
export const PANEL_BOUNDS: Record<PanelId, PanelBounds> = {
  history: { min: 220, max: 420 },
  // The document dock also holds the docked citation panel.
  document: { min: 460, max: 900 },
  settings: { min: 280, max: 480 },
};

export const DEFAULT_LAYOUT: PanelLayout = {
  slots: {
    history: { id: 'history', dock: 'left', index: 0, width: 288, visible: true },
    document: { id: 'document', dock: 'right', index: 0, width: 560, visible: false },
    settings: { id: 'settings', dock: 'right', index: 1, width: 320, visible: false },
  },
  front: null,
};

/** Versioned: the v1 model was tiled columns and must not be misread as this. */
export const LAYOUT_STORAGE_KEY = 'research:layout:v2';

export const RESEARCH_SURFACE: PanelSurface<PanelId> = {
  ids: PANEL_IDS,
  bounds: PANEL_BOUNDS,
  defaults: DEFAULT_LAYOUT,
  storageKey: LAYOUT_STORAGE_KEY,
};

/* ── bindings: the shared model, curried with the config above ────── */

export const resizePanel = (layout: PanelLayout, id: PanelId, width: number) =>
  genericResizePanel(RESEARCH_SURFACE, layout, id, width);
export const setPanelVisible = (layout: PanelLayout, id: PanelId, visible: boolean) =>
  genericSetPanelVisible(layout, id, visible);
export const togglePanel = (layout: PanelLayout, id: PanelId) =>
  genericTogglePanel(layout, id);
export const bringToFront = (layout: PanelLayout, id: PanelId) =>
  genericBringToFront(layout, id);
export const movePanel = (
  layout: PanelLayout, id: PanelId, dock: DockSide, index: number
) => genericMovePanel(RESEARCH_SURFACE, layout, id, dock, index);
export const panelsInDock = (layout: PanelLayout, dock: DockSide) =>
  genericPanelsInDock(RESEARCH_SURFACE, layout, dock);
export const dockScrolls = (layout: PanelLayout, dock: DockSide) =>
  genericDockScrolls(RESEARCH_SURFACE, layout, dock);
export const dockWidth = (layout: PanelLayout, dock: DockSide) =>
  genericDockWidth(RESEARCH_SURFACE, layout, dock);
export const readingInset = (layout: PanelLayout, dock: DockSide) =>
  genericReadingInset(RESEARCH_SURFACE, layout, dock);
export const panelZIndex = (layout: PanelLayout, id: PanelId) =>
  genericPanelZIndex(layout, id);
export const validateLayout = (raw: unknown) =>
  genericValidateLayout(RESEARCH_SURFACE, raw);
export const loadLayout = (storage?: Pick<Storage, 'getItem'>) =>
  genericLoadLayout(RESEARCH_SURFACE, storage);
export const saveLayout = (layout: PanelLayout, storage?: Pick<Storage, 'setItem'>) =>
  genericSaveLayout(RESEARCH_SURFACE, layout, storage);
export const activeSheet = (layout: PanelLayout) =>
  genericActiveSheet(RESEARCH_SURFACE, layout);
